/**
 * 67.77, smoke launchers on the vehicles (the owner: "Any vehicles that can emit smoke should probably get smoke dispensers
 * modeled, and the smoke launch corresponds to dispensers on vehicles.  Makes me think we should just have a 1, 2, 3 barrel
 * smokelauncher model that can be attached to vehicles, with current pattern probably being 2 3 barrels"): one launcher
 * model in three sizes — a block with its row of one, two or three tubes, raised and fanned (SMOKE_LAUNCHER) — and the
 * fitter that bolts a vehicle's fit (SMOKE_LAUNCHER_FITS, a list of mounts) onto its model. The sim fires each grenade from
 * the same muzzles (core/vehicleSmoke.ts smokeMuzzles; tests/smokeLauncher.test.ts holds the two together).
 *
 * The launchers ride the model's outboard fittings (`anim.fittings`: with the hull, outside its measured box), so the
 * seats and the hit shapes do not move; the bake folds them into that group's draws (no draw of their own). The far tier
 * leaves them out. Each barrel's muzzle is an anchor (`VehicleModel.smokeMuzzles`), with its breech, for the pop's flash.
 * The sheet: design_sheets/smoke_launchers.html.
 */
import * as THREE from 'three';
import { SMOKE_LAUNCHER } from '../constants';
import type { SmokeLauncherFit, SmokeLauncherMount } from '../types';
import type { UnitPalette } from './unitPalette';
import { mat, box, group, anchor, type VehicleModel } from './vehicleModels';

const BORE = '#151515';

/** A barrel's breech (on its block) and muzzle anchors: the launch leaves the muzzle along breech → muzzle. */
export interface SmokeMuzzleAnchor { breech: THREE.Object3D; muzzle: THREE.Object3D }

/**
 * One launcher of `barrels` tubes, facing its +z (the fitter turns it to its mount's yaw); its origin the foot of its
 * block. `side` (+1 a left-hand mount, −1 a right-hand one) puts the outward-facing barrel on the outer end of the row,
 * as smokeMuzzles orders them.
 */
export function buildSmokeLauncher(barrels: SmokeLauncherMount['barrels'], p: UnitPalette, side = 1): { group: THREE.Group; muzzles: SmokeMuzzleAnchor[] } {
  const L = SMOKE_LAUNCHER, g = new THREE.Group(), muzzles: SmokeMuzzleAnchor[] = [];
  const width = (barrels - 1) * L.pitch + 2 * L.barrelRadius + 0.06;
  box(g, width, L.blockHeight, 0.2, p.dark, 0, L.blockHeight / 2, 0);                                 // the block
  box(g, width + 0.04, 0.025, 0.26, p.metal, 0, 0.0125, 0);                                           // its foot plate
  const tube = new THREE.CylinderGeometry(L.barrelRadius, L.barrelRadius, L.barrelLength, 8);
  tube.translate(0, L.barrelLength / 2, 0);
  const bore = new THREE.CircleGeometry(L.barrelRadius * 0.72, 8);                                     // the dark bore: a disc over the cap
  bore.rotateX(-Math.PI / 2); bore.translate(0, L.barrelLength + 0.004, 0);                            // (4 mm proud of the tube's cap)
  const collar = new THREE.CylinderGeometry(L.barrelRadius + 0.012, L.barrelRadius + 0.012, 0.05, 8, 1, true);   // the breech's collar, its ends open (the tube fills it)
  collar.translate(0, 0.03, 0);
  for (let i = 0; i < barrels; i++) {
    const off = (i - (barrels - 1) / 2) * side;
    const b = group(g, off * L.pitch, L.blockHeight, 0);
    b.rotation.order = 'YXZ';
    b.rotation.set(Math.PI / 2 - (L.elevationDegrees * Math.PI) / 180, (off * L.spreadDegrees * Math.PI) / 180, 0);
    for (const [geo, c] of [[tube, p.body], [bore, BORE], [collar, p.metal]] as const) b.add(new THREE.Mesh(geo, mat(c)));
    muzzles.push({ breech: anchor(b, 0, 0, 0), muzzle: anchor(b, 0, L.barrelLength, 0) });
  }
  return { group: g, muzzles };
}

/**
 * Bolt `fit` onto `m`: each mount's launcher on the outboard fittings (made when the model has none) at its place, turned to
 * its yaw; the muzzles in order on `m.smokeMuzzles` (smokeMuzzles' order). Before the bake. 249: `on` a moving part instead
 * (the tank's turret: its launchers turn with it) — the fit's places are the model's at that part's rest, `on` a child of a
 * group at the model's origin (its own position subtracted).
 */
export function fitSmokeLaunchers(m: VehicleModel, fit: SmokeLauncherFit, p: UnitPalette, on?: THREE.Object3D): number {
  if (fit.length === 0) return 0;
  const fittings = on ?? m.anim.fittings ?? (m.anim.fittings = group(m.full));
  const o = on ? on.position : new THREE.Vector3();
  const out: SmokeMuzzleAnchor[] = [];
  for (const mount of fit) {
    const l = buildSmokeLauncher(mount.barrels, p, Math.sign(mount.yaw) || 1);
    l.group.position.set(mount.at[0] - o.x, mount.at[1] - o.y, mount.at[2] - o.z);
    l.group.rotation.y = (mount.yaw * Math.PI) / 180;
    fittings.add(l.group);
    out.push(...l.muzzles);
  }
  m.smokeMuzzles = out;
  return out.length;
}

/** A launcher's triangles (its block, plate and tubes): what one adds to a vehicle at its full tier. */
export function smokeLauncherTriangles(barrels: SmokeLauncherMount['barrels'], p: UnitPalette): number {
  let n = 0;
  buildSmokeLauncher(barrels, p).group.traverse(o => {
    const g = (o as THREE.Mesh).isMesh ? (o as THREE.Mesh).geometry : null;
    if (g) n += g.index ? g.index.count / 3 : g.attributes.position.count / 3;
  });
  return n;
}
